/** @format */

import { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { fetchAllDamData, fetchClientSettings } from "../api-helper/damapi";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import "../Style/damchart.css";
import { startOfWeek, addDays, parseISO } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { forwardRef } from "react";

ChartJS.register(
  LineElement,
  CategoryScale, //
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  annotationPlugin
);

function DamChart({ email, selectedMonth, setSelectedMonth }) {
  const [damData, setDamData] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [mode, setMode] = useState("month");
  const [selectedDay, setSelectedDay] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [clientSettings, setClientSettings] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [strikeValue, setStrikeValue] = useState(null);
  const [chartAnnotations, setChartAnnotations] = useState({});

  useEffect(() => {
    if (mode === "week" && !selectedWeek && availableMonths.length > 0) {
      const firstDay = new Date(`${availableMonths[0]}-07`);
      setSelectedWeek(firstDay);
    }
    if (mode === "day" && !selectedDay && damData.length > 0) {
      const day = damData[damData.length - 1].date;
      setSelectedDay(day);
    }
  }, [mode, availableMonths, selectedWeek, selectedDay, damData]);

  useEffect(() => {
    const loadSettings = async () => {
      if (!email) return;
      try {
        const data = await fetchClientSettings(email);
        setClientSettings(data.settings || []);
      } catch (err) {
        console.error("Failed to load client settings", err);
      }
    };

    loadSettings();
  }, [email]);

  useEffect(() => {
    async function loadData() {
      if (!email) return;

      const [damRaw, clientInfo] = await Promise.all([
        fetchAllDamData(),
        fetchClientSettings(email),
      ]);

      const clientMonths = new Set(clientInfo.settings.map((s) => s.month));

      const filteredData = damRaw.filter((entry) => {
        const date = new Date(entry.date);
        const monthKey = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}`;
        return clientMonths.has(monthKey);
      });

      const processedData = [];

      filteredData.forEach((entry, index) => {
        const currentDate = new Date(entry.date);
        const monthKey = `${currentDate.getFullYear()}-${String(
          currentDate.getMonth() + 1
        ).padStart(2, "0")}`;

        // Βρες όλες τις προηγούμενες (και σημερινές) ημέρες του ίδιου μήνα
        const mtdEntries = filteredData
          .slice(0, index + 1)
          .filter((e) => e.date.startsWith(monthKey));

        const allValues = mtdEntries.flatMap((e) => e.values.map((v) => v.MCP));
        const sum = allValues.reduce((acc, val) => acc + val, 0);
        const avgMCP = +(sum / allValues.length).toFixed(2);

        const strike =
          clientInfo.settings.find((s) => s.month === monthKey)?.strike ?? null;

        processedData.push({
          ...entry,
          avgMCP,
          strike,
        });
      });

      setDamData(processedData);
      setFilteredData(processedData);

      const months = [...new Set(processedData.map((e) => e.date.slice(0, 7)))];
      setAvailableMonths(months);
      setSelectedMonth(months[0]);

      const weeks = [
        ...new Set(
          processedData.map((e) => {
            const date = new Date(e.date);
            const day = date.getDay();
            const diffToMonday = (day + 6) % 7;
            const start = new Date(date);
            start.setDate(date.getDate() - diffToMonday);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            const format = (d) =>
              `${d.getFullYear()}-${(d.getMonth() + 1)
                .toString()
                .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;
            return `${format(start)} - ${format(end)}`;
          })
        ),
      ];

      setAvailableWeeks(weeks);

      const currentMonth = `${new Date().getFullYear()}-${String(
        new Date().getMonth() + 1
      ).padStart(2, "0")}`;
      const strike = clientInfo.settings.find(
        (s) => s.month === currentMonth
      )?.strike;
      setStrikeValue(strike);
    }

    loadData();
  }, []);

  // Φιλτράρισμα βάσει ημερομηνιών
  const createAnnotation = (strike, id) => ({
    type: "line",
    yMin: strike,
    yMax: strike,
    borderColor: "#DC3545",
    borderWidth: 2,
    label: {
      display: true,
      content: `Strike ${strike}`,
      position: "start",
      backgroundColor: "#DC3545",
      color: "#fff",
      font: {
        style: "bold",
        size: 10,
      },
    },
    backgroundColor: "rgba(220, 53, 69, 0.1)", // Απαλό κόκκινο κάτω από τη γραμμή
    drawTime: "afterDatasetsDraw",
  });

  const getChartData = () => {
    if (!damData.length) return { labels: [], datasets: [], annotations: {} };

    let labels = [];
    let data = [];
    let strikeData = [];
    let annotations = {};
    let filtered = [];

    if (mode === "day" && selectedDay) {
      const entry = damData.find((e) => e.date === selectedDay);
      if (entry) {
        labels = Array.from(
          { length: 24 },
          (_, i) => `${i.toString().padStart(2, "0")}:00`
        );
        data = Array.from({ length: 24 }, (_, i) => {
          const hourData = entry.values.find((v) => v.hour === i + 1);
          return hourData ? hourData.MCP : null;
        });
        const monthKey = selectedDay.slice(0, 7);
        const strike = entry.strike;
        if (strike != null) {
          annotations[monthKey] = createAnnotation(strike, monthKey);
        }
      }
    } else {
      if (mode === "month" && selectedMonth) {
        filtered = damData.filter((entry) =>
          entry.date.startsWith(selectedMonth)
        );
      } else if (mode === "week" && selectedWeek) {
        const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);

        filtered = damData.filter((entry) => {
          const entryDate = parseISO(entry.date);
          return entryDate >= weekStart && entryDate <= weekEnd;
        });
      } else {
        filtered = damData;
      }

      labels = filtered.map((entry) => entry.date);
      data = filtered.map(
        (entry) => (entry.avgMCP != null ? entry.avgMCP : NaN) // ή 0 αν προτιμάς
      );
      filtered.forEach((entry) => {
        const monthKey = entry.date.slice(0, 7);
        const strike = entry.strike;
        if (strike != null && !annotations[monthKey]) {
          annotations[monthKey] = createAnnotation(strike, monthKey);
        }
      });
    }

    return {
      labels,
      datasets: [
        {
          label:
            mode === "day"
              ? `DAM Price per hour for ${selectedDay}`
              : "MtD AVG DAM Price per Day",
          data,
          borderColor: "#0076BE",
          tension: 0.4,
          pointBackgroundColor: "#00205B",
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
      annotations,
    };
  };

  const { labels, datasets, annotations } = getChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#00205B",
          font: {
            size: 14,
            weight: "bold",
          },
        },
      },
      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00205B",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        borderColor: "#0076BE",
        borderWidth: 1,
        titleFont: { size: 14, weight: "bold" },
        bodyFont: { size: 13 },
        padding: 10,
      },

      annotation: {
        annotations,
      },
    },
    scales: {
      x: {
        ticks: {
          color: "#00205B",
          maxRotation: 45,
          minRotation: 30,
        },
        title: {
          display: true,
          text: mode === "day" ? "Hour" : "Date",
          color: "#00205B",
          font: {
            weight: "bold",
          },
        },
      },
      y: {
        ticks: {
          color: "#00205B",
        },
        title: {
          display: true,
          text: "DAM Price (€)",
          color: "#00205B",
          font: {
            weight: "bold",
          },
        },
        beginAtZero: false,
      },
    },
  };

  const WeekRangeInput = forwardRef(({ value, onClick, selected }, ref) => {
    if (!selected)
      return (
        <input
          ref={ref}
          onClick={onClick}
          className='custom-input'
          placeholder='Select week'
          readOnly
        />
      );

    const start = startOfWeek(selected, { weekStartsOn: 1 });
    const end = addDays(start, 6);

    const format = (date) =>
      date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

    return (
      <input
        ref={ref}
        onClick={onClick}
        value={`${format(start)} - ${format(end)}`}
        className='custom-input'
        readOnly
      />
    );
  });

  return (
    <div className='chart-container'>
      <h2 className='chart-title'>DAM and Strike Price</h2>

      <div className='chart-filters'>
        <select
          className='custom-select'
          value={mode}
          onChange={(e) => setMode(e.target.value)}
        >
          <option value='month'>Month</option>
          <option value='week'>Week</option>
          <option value='day'>Day</option>
        </select>

        {mode === "day" && (
          <>
            <DatePicker
              selected={selectedDay ? new Date(selectedDay) : null}
              onChange={(date) =>
                setSelectedDay(date.toISOString().slice(0, 10))
              }
              dateFormat='yyyy-MM-dd'
              placeholderText='Select date'
              className='custom-input'
              calendarStartDay={1}
            />
          </>
        )}

        {mode === "month" && (
          <>
            <select
              className='custom-select'
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {availableMonths.length === 0 && (
                <option value=''>No available months</option>
              )}

              {availableMonths.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
          </>
        )}

        {mode === "week" && (
          <>
            <DatePicker
              selected={selectedWeek}
              onChange={(date) => setSelectedWeek(date)}
              showWeekPicker
              calendarStartDay={1}
              customInput={<WeekRangeInput selected={selectedWeek} />}
            />
          </>
        )}
      </div>

      <div className='chart-wrapper'>
        <Line
          data={getChartData()}
          options={chartOptions}
          plugins={[annotationPlugin]}
        />
      </div>
    </div>
  );
}

export default DamChart;
